import React, { useEffect, useMemo, useState } from 'react';
import * as Routes from '@/modules/routes';
import useFlags from '@/hooks/useFlags';
import useLiveSession from '@/channels/liveSession/useLiveSession';
import useLiveSessionStore from '@/channels/liveSession/liveSessionStore';
import useStudentLiveSession from '@/channels/studentLiveSession/useStudentLiveSession';
import { logLiveSessionEvent } from '@/modules/liveSessionEvents';
import RemovedFromSessionPopup from '@/components/student/RemovedFromSessionPopup';
import SessionEndedPopup from '@/components/student/SessionEndedPopup';
import TeachingSessionStartedPopup from '@/components/student/TeachingSessionStartedPopup';

// AIDEV-NOTE: PLAT-16886 — initial state is seeded from server props
// (LiveSessions::SessionManager#active_session_payload_for_student); the server returns nil
// once a LiveSessionParticipation row exists. The SSR seed is suppressed on /present_mode pages
// where useStudentSlideSync drives the experience.
// PLAT-17536 — the realtime join popup still shows on /present_mode (relabeled per the slideshow
// the student is on); useStudentSlideSync owns the session lifecycle there.

interface JoinParams {
  parent_id: number;
  parent_type: string;
  program_id: number;
  session_id: string;
  slide_show_id: number;
}

interface Props {
  currentSlideShowId?: number;
  parentId?: number;
  parentType?: string;
  programId?: number;
  sessionId?: string;
  slideShowId?: number;
}

type OpenPopup = 'started' | 'ended' | 'removed' | null;

const isOnPresentMode = () => window.location.pathname.includes('/present_mode');

const StudentSessionListener = ({
  currentSlideShowId,
  parentId,
  parentType,
  programId,
  sessionId,
  slideShowId,
}: Props) => {
  const { teacherPacedMode } = useFlags();

  const initialJoinParams: JoinParams | null = useMemo(() => (
    (sessionId
      && slideShowId != null
      && programId != null
      && parentId != null
      && parentType
      && !isOnPresentMode())
      ? {
        parent_id: parentId,
        parent_type: parentType,
        program_id: programId,
        session_id: sessionId,
        slide_show_id: slideShowId,
      }
      : null
  ), [parentId, parentType, programId, sessionId, slideShowId]);

  const [openPopup, setOpenPopup] = useState<OpenPopup>(initialJoinParams ? 'started' : null);
  const [joinParams, setJoinParams] = useState<JoinParams | null>(initialJoinParams);

  const {
    activeSessionId,
    joinNotification,
    removalNotification,
    setActiveSessionId,
  } = useStudentLiveSession({ enabled: teacherPacedMode });

  // Seed the store from SSR so LiveSessionChannel opens on mount without waiting for a
  // fresh join_notification.
  useEffect(() => {
    if (!teacherPacedMode) return;
    if (initialJoinParams?.session_id) {
      setActiveSessionId(initialJoinParams.session_id);
      logLiveSessionEvent(initialJoinParams.session_id, 'join_popup_shown', {
        dismiss_only: false,
        origin: 'ssr_seed',
        slide_show_id: initialJoinParams.slide_show_id,
      });
    }
  }, [teacherPacedMode, initialJoinParams, setActiveSessionId]);

  useEffect(() => {
    if (!teacherPacedMode) return;
    if (!joinNotification?.session_id) return;
    // PLAT-17536 — the non-dismissable popup would force-navigate a locked-in student out of
    // their session, so suppress a different-slideshow invite for them (not for free-browsers).
    // Read the lock imperatively: it's only the value at notification time that matters.
    const lockedIntoSession = !!useLiveSessionStore.getState().sessionId;
    const sameSlideShow = joinNotification.slide_show_id === currentSlideShowId;
    if (isOnPresentMode() && lockedIntoSession && !sameSlideShow) {
      logLiveSessionEvent(joinNotification.session_id, 'join_notification_suppressed', {
        current_slide_show_id: currentSlideShowId ?? null,
        notified_slide_show_id: joinNotification.slide_show_id,
        reason: 'locked_into_other_session',
      });
      return;
    }
    // Store-side isJoinNotificationValid filters incomplete payloads; the !-assertions are
    // safe here.
    setJoinParams({
      parent_id: joinNotification.parent_id!,
      parent_type: joinNotification.parent_type!,
      program_id: joinNotification.program_id!,
      session_id: joinNotification.session_id,
      slide_show_id: joinNotification.slide_show_id!,
    });
    setOpenPopup('started');
    logLiveSessionEvent(joinNotification.session_id, 'join_popup_shown', {
      dismiss_only: isOnPresentMode() && sameSlideShow,
      origin: 'broadcast',
      slide_show_id: joinNotification.slide_show_id,
    });
  }, [teacherPacedMode, joinNotification, currentSlideShowId]);

  useEffect(() => {
    if (!teacherPacedMode) return;
    if (!removalNotification) return;
    setOpenPopup('removed');
    setJoinParams(null);
  }, [teacherPacedMode, removalNotification]);

  // Own the subscription only off /present_mode (useStudentSlideSync owns it there — PLAT-17536),
  // but read the ended signal from the refcounted store regardless of which tree holds the sub.
  useLiveSession(isOnPresentMode() ? null : activeSessionId);
  const sessionEndedReason = useLiveSessionStore(state => state.sessionEndedReason);
  const clearSessionEnded = useLiveSessionStore(state => state.clearSessionEnded);

  useEffect(() => {
    if (!teacherPacedMode) return;
    if (!sessionEndedReason) return;
    setOpenPopup('ended');
    setJoinParams(null);
    setActiveSessionId(null);
  }, [teacherPacedMode, sessionEndedReason, setActiveSessionId]);

  if (!teacherPacedMode) return null;

  // Already viewing the session's slideshow — useStudentSlideSync handles the lock, so the
  // popup just closes instead of navigating.
  const onCurrentSlideShow = isOnPresentMode()
    && !!currentSlideShowId
    && joinParams?.slide_show_id === currentSlideShowId;

  const handleJoinClick = joinParams
    ? () => {
      if (onCurrentSlideShow) {
        setOpenPopup(null);
        logLiveSessionEvent(joinParams.session_id, 'join_popup_dismissed', { dismiss_only: true });
        return;
      }
      // `via` is read by present_mode's forensic log to distinguish a popup join from a direct URL.
      const targetUrl = Routes.present_mode_shared_slide_show_path(joinParams.slide_show_id, {
        parent_id: joinParams.parent_id,
        parent_type: joinParams.parent_type,
        program_id: joinParams.program_id,
        via: 'popup',
      });
      logLiveSessionEvent(joinParams.session_id, 'join_popup_join_clicked', { target_url: targetUrl });
      window.location.assign(targetUrl);
    }
    : undefined;

  return (
    <>
      <TeachingSessionStartedPopup
        dismissOnly={onCurrentSlideShow}
        isOpen={openPopup === 'started'}
        onJoinClick={handleJoinClick}
      />
      <SessionEndedPopup
        isOpen={openPopup === 'ended'}
        onClose={() => { setOpenPopup(null); clearSessionEnded(); }}
      />
      <RemovedFromSessionPopup
        isOpen={openPopup === 'removed'}
        onClose={() => setOpenPopup(null)}
      />
    </>
  );
};

export default StudentSessionListener;
